import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useFeaturedPackages } from '../hooks/useFeaturedPackages'
import { useDestinations } from '../hooks/useDestinations'
import SEO from '../components/SEO'
import { buildWebPageSchema } from '../utils/schema'
import { RouteLayout, RouteSection } from '../components/RouteLayout'
import PackageCard, { PackageCardSkeleton } from '../components/PackageCard'
import { getWhatsAppUrl } from '../utils/whatsapp'
import { getBrandLogoUrl } from '../components/BrandMark'

const HERO_SLIDES = [
  { name: 'France', image: '/hero/france.jpg' },
  { name: 'Bali', image: '/hero/bali.jpg' },
  { name: 'Mauritius', image: '/hero/mauritius.jpg' },
  { name: 'South Africa', image: '/hero/south-africa.jpg' },
]

const POPULAR_DESTINATIONS = [
  { name: 'France', id: 47, image: '/destinations/france.jpg' },
  { name: 'Bali', id: 124, image: '/destinations/bali.jpg' },
  { name: 'Mauritius', id: 11, image: '/destinations/mauritius.jpg' },
  { name: 'South Africa', id: 97, image: '/destinations/south-africa.jpg' },
]

export default function Home() {
  const { franchiseData, loading } = useFranchiseData()
  const { featuredPackages, loading: packagesLoading, error: packagesError } = useFeaturedPackages()
  const { destinations } = useDestinations()
  const brandName = franchiseData?.name || 'VAIA World'
  const whatsappUrl = getWhatsAppUrl(franchiseData, 'Hi, I would like to plan a trip with VAIA World.')
  const [heroIndex, setHeroIndex] = useState(0)
  const popularDestinations = POPULAR_DESTINATIONS.map((popular) => {
    const match = destinations.find(
      (destination) => destination.name.toLowerCase() === popular.name.toLowerCase()
    )
    return match
      ? { id: match.id, name: match.name, image: popular.image }
      : popular
  })

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((current) => (current + 1) % HERO_SLIDES.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  if (loading || !franchiseData) {
    return (
      <div className="bg-cream min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-line rounded w-64 mx-auto mb-4" />
          <div className="h-4 bg-line rounded w-48 mx-auto" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-cream">
      <SEO
        title={`${brandName} | Let us walk you through the world`}
        description="Personal Lifestyle Planners design seamless trips — flights, stays and visa guidance — so you travel with confidence, not paperwork stress."
        canonical={typeof window !== 'undefined' ? window.location.origin : undefined}
        openGraph={{
          type: 'website',
          title: `${brandName} | Let us walk you through the world`,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          image: getBrandLogoUrl(),
          siteName: brandName,
        }}
        twitter={{ card: 'summary_large_image' }}
        jsonLd={buildWebPageSchema({
          name: `${brandName} Home`,
          description: 'Personal Lifestyle Planners design seamless trips — flights, stays and visa guidance.',
          url: typeof window !== 'undefined' ? window.location.href : ''
        })}
      />

      <section className="hero-full" aria-label="Hero">
        <div className="hero-carousel" aria-hidden="true">
          {HERO_SLIDES.map((slide, index) => (
            <img
              key={slide.image}
              src={slide.image}
              alt=""
              className={index === heroIndex ? 'is-active' : ''}
            />
          ))}
        </div>
        <div className="hero-carousel-dots">
          {HERO_SLIDES.map((slide, index) => (
            <button
              key={slide.name}
              type="button"
              className={index === heroIndex ? 'is-active' : ''}
              aria-label={`Show ${slide.name}`}
              onClick={() => setHeroIndex(index)}
            />
          ))}
        </div>

        <div className="hero-full-content">
          <div className="eyebrow">The Travel People</div>
          <h1 className="text-[clamp(38px,6vw,64px)] leading-[1.02] max-w-[820px] m-0">
            Let us walk you<br />through the world.
          </h1>
          <p className="hero-copy text-lg max-w-[560px] mt-5 leading-relaxed">
            Personal Lifestyle Planners design seamless trips — flights, stays and visa guidance — so you travel with confidence, not paperwork stress.
          </p>
          <div className="flex gap-3.5 mt-8 flex-wrap">
            <a
              href={whatsappUrl}
              target={whatsappUrl.startsWith('http') ? '_blank' : undefined}
              rel={whatsappUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="btn-primary"
            >
              Plan My Trip
            </a>
            <Link to="/packages" className="btn-ghost">
              Browse Packages
            </Link>
          </div>

          <div className="trust-strip">
            <div className="trust-item">
              <div className="num">01</div>
              <p>Real Lifestyle Planners</p>
            </div>
            <div className="trust-item">
              <div className="num">02</div>
              <p>Visa Support Included</p>
            </div>
            <div className="trust-item">
              <div className="num">03</div>
              <p>Flexible Payment Plans</p>
            </div>
            <div className="trust-item">
              <div className="num">04</div>
              <p>WhatsApp Support</p>
            </div>
          </div>
        </div>
      </section>

      <RouteLayout>
        <RouteSection mark={2} className="!pt-[64px]" id="offers">
          <div className="sec-head">
            <h2>Featured Escapes</h2>
            <Link to="/packages" className="sec-link">See all packages →</Link>
          </div>

          {packagesLoading ? (
            <div className="cards">
              {[1, 2, 3].map((i) => (
                <PackageCardSkeleton key={i} />
              ))}
            </div>
          ) : packagesError ? (
            <div className="text-center py-8">
              <h3 className="text-lg font-semibold text-forest">Unable to load packages</h3>
              <p className="mt-2 text-sm text-ink-soft">{packagesError}</p>
              <button onClick={() => window.location.reload()} className="btn-primary mt-4">
                Try Again
              </button>
            </div>
          ) : featuredPackages.length === 0 ? (
            <div className="text-center py-8">
              <h3 className="text-lg font-semibold text-forest">No featured packages available</h3>
              <p className="mt-2 text-sm text-ink-soft">Check back soon for our latest travel deals.</p>
              <Link to="/packages" className="btn-primary mt-4">View All Packages</Link>
            </div>
          ) : (
            <div className="cards">
              {featuredPackages.slice(0, 3).map((pkg, index) => (
                <PackageCard key={pkg.id} pkg={pkg} index={index} />
              ))}
            </div>
          )}
        </RouteSection>

        <RouteSection mark={3} className="!pt-0">
          <div className="visa-teaser">
            <div>
              <h2>Never lose sleep over visa paperwork.</h2>
              <p>Every VAIA World booking includes dedicated visa guidance — checklists, timelines, and a real person to ask.</p>
            </div>
            <Link to="/visa-support" className="btn-primary">See Visa Support</Link>
          </div>
        </RouteSection>

        <RouteSection mark={4} className="!pt-0">
          <div className="sec-head">
            <h2>Popular Destinations</h2>
            <Link to="/packages" className="sec-link">See all →</Link>
          </div>
          <div className="dest-grid">
            {popularDestinations.map((destination) => (
              <Link
                key={destination.id}
                to={`/packages?destination=${destination.id}`}
                className="dest-item"
                style={{ backgroundImage: `url(${destination.image})` }}
              >
                <span>{destination.name}</span>
              </Link>
            ))}
          </div>
        </RouteSection>
      </RouteLayout>
    </div>
  )
}
